export const LotTypeEnum = {
    ALL_LOCATIONS: 0,
    SERVICE_PROVIDER: 1,
    OEM_SERVICE: 2,
    THIRD_PARTY: 3,
    STANDARD: 4
};

export const LotTypeEnumList = [
    { id: 0, name: "All Locations" },
    { id: 1, name: "Service Provider" },
    { id: 2, name: "OEM Service Provider" },
    { id: 3, name: "3rd Party Lot" },
    { id: 4, name: "Standard Lot" }
];

export const LotTypeEnumListOptions = [
    { id: '0', name: "All Locations" },
    { id: '1', name: "Service Provider" },
    { id: '2', name: "OEM Service Provider" },
    { id: '3', name: "3rd Party Lot" },
    { id: '4', name: "Standard Lot" }
];

// SCN-633 -- matches PMF.Domain.Common.Enums.LotStatus (parqai-api). Do NOT
// renumber -- these values are sent by the API.
export const LotStatusEnum = {
    ACTIVE: 0,
    CLOSED: 1,
    DEPRECATED: 2,
    DELETED: 3,
};

// SCN-633 -- statuses excluded from OPERATIONAL lot selectors (new-work context
// only: Load creation, vehicle check-in/assignment). The full catalog, every
// status included, must keep working everywhere else (billing, reports, the
// admin Lots page, LotDialog edit) -- this is deliberately an opt-in filter
// applied only at specific call sites, never inside GetAllLotsQuery/useGetLotsQuery
// itself, which ~130 other screens share. Mirrors parqai-field's
// NON_SELECTABLE_LOT_STATUSES (workingLotClaims.ts) so both clients agree on
// what "operational" means for a lot.
export const NON_OPERATIONAL_LOT_STATUSES = [
    LotStatusEnum.CLOSED,
    LotStatusEnum.DEPRECATED,
    LotStatusEnum.DELETED,
];

// Excludes Closed/Deprecated/Deleted lots from a catalog meant for selecting
// a lot for NEW work. A lot with no status field (data synced before PR-6, or
// a stale cache) is treated as selectable, matching resolveLotStatus's own
// default and parqai-field's filterSelectableLots -- never fail closed on
// unknown status for a list filter. `keepIds` (a single id or array) keeps an
// already-assigned lot visible/selectable even if closed, so an existing
// operation never loses access to its current lot.
export function filterOperationalLots(lots, keepIds) {
    const keep = new Set(
        (Array.isArray(keepIds) ? keepIds : keepIds != null ? [keepIds] : []).map(String)
    );
    return (lots ?? []).filter(lot =>
        keep.has(String(lot.id)) ||
        lot.status === undefined ||
        lot.status === null ||
        !NON_OPERATIONAL_LOT_STATUSES.includes(lot.status)
    );
}

export const LotStatusEnumList = [
    { id: LotStatusEnum.ACTIVE, name: "Active" },
    { id: LotStatusEnum.CLOSED, name: "Closed" },
    { id: LotStatusEnum.DEPRECATED, name: "Deprecated" },
    { id: LotStatusEnum.DELETED, name: "Deleted" },
];

// A lot synced/fetched from an API response that predates PR-6 (status DTO
// exposure) has no `status` field at all -- treat that as the database
// default (Active) for DISPLAY purposes (the status chip), rather than
// showing a blank/unknown badge.
export const resolveLotStatus = (status) =>
    status === undefined || status === null ? LotStatusEnum.ACTIVE : status;

// Pure predicates for the lifecycle action visibility rules (05-web-design.md:
// "Display Close only for Active lots" / "Display Deprecate only for Closed lots") --
// extracted so they are unit-testable without rendering LotDialog.
//
// Deliberately check the RAW status, NOT resolveLotStatus(status): a missing/unknown
// status must never enable either action, even though it displays as "Active" in the
// chip above. Defaulting an unknown status to enabling a write action would be unsafe
// (the lot's real status could be Closed/Deprecated/Deleted) -- display-time leniency
// and action-enablement safety are deliberately different checks.
export const canCloseLot = (status) => status === LotStatusEnum.ACTIVE;
export const canDeprecateLot = (status) => status === LotStatusEnum.CLOSED;

// Pure outcome classification for a Close/Deprecate RTK Query mutation result --
// extracted from LotDialog so the 403/409 handling logic is unit-testable without
// rendering or mocking RTK Query/Redux.
export const LifecycleTransitionOutcome = {
    SUCCESS: 'success',
    UNAUTHORIZED: 'unauthorized',
    CONFLICT: 'conflict',
    ERROR: 'error',
};

export const interpretLifecycleTransitionResult = (res) => {
    if (!res?.error) {
        return { outcome: LifecycleTransitionOutcome.SUCCESS };
    }
    const httpStatus = res.error?.status;
    if (httpStatus === 403) {
        return { outcome: LifecycleTransitionOutcome.UNAUTHORIZED };
    }
    if (httpStatus === 409) {
        const serverMessage = typeof res.error?.data === 'string' ? res.error.data : undefined;
        return { outcome: LifecycleTransitionOutcome.CONFLICT, serverMessage };
    }
    return { outcome: LifecycleTransitionOutcome.ERROR };
};

// Pure "what should the dialog's local lot state become" logic, extracted so the
// status-refresh behavior (success -> locally apply the requested status; conflict ->
// resync from the server's real current value; anything else -> leave unchanged) is
// unit-testable without rendering.
export const resolveLotAfterTransition = ({ lot, requestedStatus, outcome, freshLot }) => {
    if (outcome === LifecycleTransitionOutcome.SUCCESS) {
        return { ...lot, status: requestedStatus };
    }
    if (outcome === LifecycleTransitionOutcome.CONFLICT && freshLot) {
        return { ...lot, status: resolveLotStatus(freshLot.status) };
    }
    return lot;
};

export const ScreenViewTypes = {
    MAP: 1,
    LIST: 2,
    CAPACITY: 3,
};

export const CapacityTableTypes = {
    PER_CLIENT: 1,
    PER_ASSET_CLASS: 2,
    PER_ASSET_CLASS_PER_CLIENT: 3,
};

export const CapacityTableTypesList = [
    {
        id: CapacityTableTypes.PER_CLIENT,
        name: "Capacity Per Client"
    },
    {
        id: CapacityTableTypes.PER_ASSET_CLASS,
        name: "Capacity Per Asset Class"
    },
    {
        id: CapacityTableTypes.PER_ASSET_CLASS_PER_CLIENT,
        name: "Capacity Per Asset Class Per Client"
    },
];